import { UploadHelper, PersonInterface } from "..";
import { ArrayHelper } from "@churchapps/apphelper";
import {
  ImportHelper
  , ImportCampusInterface, ImportServiceInterface
  , ImportGroupServiceTimeInterface
  , ImportVisitInterface, ImportSessionInterface
  , ImportDonationBatchInterface, ImportFundInterface
  , ImportDataInterface
  , ImportHouseholdInterface
} from "../ImportHelper";
import Papa from "papaparse";
import { runStep, compressZip } from "./RunStep";

const generateB1Zip = async (importData: ImportDataInterface, updateProgress: (name: string, status: string) => void) => {
  const files: { name: string, contents: any }[] = [];

  const runImport = (keyName: string, code: () => Promise<void>) => runStep(keyName, updateProgress, code);

  files.push({ name: "services.csv", contents: await exportCampuses(importData, runImport) });

  files.push({ name: "people.csv", contents: await exportPeople(importData, runImport) });

  exportPhotos(importData.people, files, runImport);

  files.push({ name: "groups.csv", contents: await exportGroups(importData, runImport) });

  files.push({ name: "groupmembers.csv", contents: await exportGroupMembers(importData, runImport) });

  files.push({ name: "donations.csv", contents: await exportDonations(importData, runImport) });

  files.push({ name: "attendance.csv", contents: await exportAttendance(importData, runImport) });

  files.push({ name: "forms.csv", contents: await exportForms(importData, runImport) });

  files.push({ name: "questions.csv", contents: await exportQuestions(importData, runImport) });

  files.push({ name: "formSubmissions.csv", contents: await exportFormSubmissions(importData, runImport) });

  files.push({ name: "answers.csv", contents: await exportAnswers(importData, runImport) });

  runStep("Compressing", updateProgress, () => compressZip(files, "B1Export.zip"));

};
const exportCampuses = async (importData: ImportDataInterface, runImport: (keyName: string, code: () => Promise<void>) => Promise<void>) => {
  const { campuses, services, serviceTimes } = importData;
  const data: any[] = [];
  await runImport("Campuses/Services/Times", async () => {
    serviceTimes.forEach((st) => {
      const service: ImportServiceInterface = ImportHelper.getById(services, st.serviceId);
      const campus: ImportCampusInterface = ImportHelper.getById(campuses, service.campusId);
      const row = { importKey: st.id, campus: campus.name, service: service.name, time: st.name };
      data.push(row);
    });
  });
  return Papa.unparse(data);
};

const exportGroupMembers = async (importData: ImportDataInterface, runImport: (keyName: string, code: () => Promise<void>) => Promise<void>) => {
  const { groupMembers } = importData;
  const data: any[] = [];
  await runImport("Group Members", async () => {
    groupMembers.forEach((gm) => {
      const row = { groupKey: gm.groupId, personKey: gm.personId };
      data.push(row);
    });
  });
  return Papa.unparse(data);
};

const exportPeople = async (importData: ImportDataInterface, runImport: (keyName: string, code: () => Promise<void>) => Promise<void>) => {
  const tmpHouseholds: ImportHouseholdInterface[] = [...importData.households];
  const data: any[] = [];
  const { people } = importData;
  await runImport("People", async () => {
    people.forEach((p) => {
      const household = tmpHouseholds.find(h => p.householdKey === h.importKey);
      const row = {
        importKey: p.importKey,
        householdName: household.name ?? p.name.last,
        householdRole: p.householdRole ?? "",
        displayName: p.name.display ?? "",
        lastName: p.name.last,
        firstName: p.name.first,
        middleName: p.name.middle,
        nickName: p.name.nick,
        prefix: p.name.title ?? "",
        suffix: p.name.suffix ?? "",
        birthDate: p.birthDate,
        gender: p.gender,
        maritalStatus: p.maritalStatus,
        membershipStatus: p.membershipStatus,
        homePhone: p.contactInfo.homePhone,
        mobilePhone: p.contactInfo.mobilePhone,
        workPhone: p.contactInfo.workPhone,
        email: p.contactInfo.email,
        address1: p.contactInfo.address1,
        address2: p.contactInfo.address2,
        city: p.contactInfo.city,
        state: p.contactInfo.state,
        zip: p.contactInfo.zip,
        photo: (p.photoUpdated === undefined) ? "" : p.id.toString() + ".png"
      };
      data.push(row);
    });
  });
  return Papa.unparse(data);
};

const exportGroups = async (importData: ImportDataInterface, runImport: (keyName: string, code: () => Promise<void>) => Promise<void>) => {
  const { groups, groupServiceTimes } = importData;
  const data: any[] = [];
  await runImport("Groups", async () => {
    groups.forEach((g) => {
      let serviceTimeIds: string[] = [];
      const gst: ImportGroupServiceTimeInterface[] = ArrayHelper.getAll(groupServiceTimes, "groupId", g.id);
      if (gst.length === 0) serviceTimeIds = [""];
      else gst.forEach((time) => { serviceTimeIds.push(time.serviceTimeId.toString()); });
      serviceTimeIds.forEach((serviceTimeId) => {
        const row = { importKey: g.id, serviceTimeKey: serviceTimeId, categoryName: g.categoryName, name: g.name, trackAttendance: g.trackAttendance ? "TRUE" : "FALSE" };
        data.push(row);
      });
    });
  });
  await runImport("Group Service Times", async () => {
  });
  return Papa.unparse(data);
};

const exportDonations = async (importData: ImportDataInterface, runImport: (keyName: string, code: () => Promise<void>) => Promise<void>) => {
  const { funds, batches, donations } = importData;
  const data: any[] = [];

  await runImport("Funds", async () => {
  });

  await runImport("Donation Batches", async () => {
  });

  await runImport("Donations", async () => {
    donations.forEach((donation) => {
      const fund: ImportFundInterface = ImportHelper.getById(funds, donation.fund?.id);
      const batch: ImportDonationBatchInterface = ImportHelper.getById(batches, donation.batchId);
      const row = {
        batch: batch ? batch.id : "",
        date: donation.donationDate,
        personKey: donation.person?.id,
        method: donation.method,
        methodDetails: donation.methodDetails,
        amount: donation.amount,
        fund: fund ? fund.name : "",
        notes: donation.notes
      };
      data.push(row);
    });
  });

  await runImport("Donation Funds", async () => {
  });
  return Papa.unparse(data);
};

const exportAttendance = async (importData: ImportDataInterface, runImport: (keyName: string, code: () => Promise<void>) => Promise<void>) => {
  const { sessions, visits, visitSessions } = importData;
  const data: any[] = [];
  await runImport("Attendance", async () => {
    visitSessions.forEach((vs) => {
      const visit: ImportVisitInterface = ImportHelper.getById(visits, vs.visitId);
      const session: ImportSessionInterface = ImportHelper.getById(sessions, vs.sessionId);
      if (visit && session) {
        const row = { date: visit.visitDate, serviceTimeKey: session.serviceTimeId, groupKey: session.groupId, personKey: visit.personId };
        data.push(row);
      }
    });
  });
  return Papa.unparse(data);
};

const exportForms = async (importData: ImportDataInterface, runImport: (keyName: string, code: () => Promise<void>) => Promise<void>) => {
  const { forms } = importData;
  const data: any[] = [];
  await runImport("Forms", async () => {
    forms.forEach((f) => {
      const row = { importKey: f.id, name: f.name, contentType: f.contentType };
      data.push(row);
    });
  });
  return Papa.unparse(data);
};
const exportQuestions = async (importData: ImportDataInterface, runImport: (keyName: string, code: () => Promise<void>) => Promise<void>) => {
  const { questions } = importData;
  const data: any[] = [];
  await runImport("Questions", async () => {
    questions.forEach(q => {
      const row = { questionKey: q.id, formKey: q.formId, fieldType: q.fieldType, title: q.title };
      data.push(row);
    });
  });
  return Papa.unparse(data);
};
const exportAnswers = async (importData: ImportDataInterface, runImport: (keyName: string, code: () => Promise<void>) => Promise<void>) => {
  const { answers } = importData;
  const data: any[] = [];
  await runImport("Answers", async () => {
    answers.forEach(a => {
      const row = { questionKey: a.questionId, formSubmissionKey: a.formSubmissionId, value: a.value };
      data.push(row);
    });
  });
  return Papa.unparse(data);
};
const exportFormSubmissions = async (importData: ImportDataInterface, runImport: (keyName: string, code: () => Promise<void>) => Promise<void>) => {
  const { formSubmissions } = importData;
  const data: any[] = [];
  await runImport("Form Submissions", async () => {
    formSubmissions.forEach(fs => {
      // importKey carries the submission id so answers.csv (which references it) can be
      // matched back to the right submission on re-import instead of leaking across the form.
      const row = { importKey: fs.id, formKey: fs.formId, personKey: fs.contentId, contentType: fs.contentType };
      data.push(row);
    });
  });
  return Papa.unparse(data);
};

const exportPhotos = async (people: PersonInterface[], files: { name: string, contents: string | Buffer }[], runImport: (keyName: string, code: () => Promise<void>) => Promise<void>) => {
  await runImport("Photos", async () => {
    const result: Promise<any>[] = [];
    people.forEach(async (p) => {
      if (p.photoUpdated !== undefined) result.push(UploadHelper.downloadImageBytes(files, p.id.toString() + ".png", p.photo));
    });
    Promise.all(result);
  });
};

export default generateB1Zip;
